
import { useResumeUpload } from "./useResumeUpload";
import { useResumeSelection } from "./useResumeSelection";
import { useJobDetails } from "./useJobDetails";
import { useOptimizationProcess } from "./useOptimizationProcess";
import { useResumeOperations } from "@/hooks/useResumeOperations";

export const useResumeForm = (onTabChange: (tab: string) => void) => {
  // Get resume upload state and handlers
  const { 
    isUploading, 
    setIsUploading,
    resumeContent, 
    originalFilename,
    uploadComplete,
    isDuplicateResume,
    handleUploadComplete,
    resetUpload
  } = useResumeUpload();

  // Get job details state
  const {
    jobTitle,
    company,
    jobDescription,
    setJobTitle,
    setCompany,
    setJobDescription,
    resetJobDetails
  } = useJobDetails();

  // Handle resume content updates when selecting a resume
  const handleResumeContentUpdate = (content: string, filename: string) => {
    resetUpload();
    if (content && filename) {
      handleUploadComplete(content, filename);
    }
  };

  // Get resume selection state and handlers
  const {
    selectedResumeId,
    originalResumes,
    isLoadingOriginalResumes,
    refetchOriginalResumes,
    handleResumeSelect
  } = useResumeSelection(handleResumeContentUpdate);

  // Get resume operations (e.g., deletion)
  const { handleDeleteResume, checkDuplicateResume } = useResumeOperations();

  // Get optimization process state and handlers
  const {
    isProcessing,
    apiError,
    processOptimization
  } = useOptimizationProcess(onTabChange, resetJobDetails);

  // Check if form is complete and valid
  const isFormValid = resumeContent && jobTitle && company && jobDescription && uploadComplete;

  // Handle deleting a resume
  const handleDeleteFromForm = async (resumeId: string) => {
    const resumeToDelete = originalResumes.find(resume => resume.id === resumeId);
    if (resumeToDelete) {
      const success = await handleDeleteResume(resumeToDelete);
      if (success) {
        if (resumeId === selectedResumeId) {
          resetUpload();
        }
        refetchOriginalResumes();
      }
    }
  };

  // Handle the optimization process
  const handleOptimize = async () => {
    return processOptimization(
      resumeContent,
      jobTitle,
      company,
      jobDescription,
      selectedResumeId,
      originalFilename
    );
  };

  return {
    isUploading,
    setIsUploading,
    isProcessing,
    resumeContent,
    originalFilename,
    jobTitle,
    company,
    jobDescription,
    uploadComplete,
    apiError,
    selectedResumeId,
    isDuplicateResume,
    isFormValid,
    originalResumes,
    isLoadingOriginalResumes,
    handleUploadComplete,
    handleResumeSelect,
    handleDeleteFromForm,
    handleOptimize,
    setJobTitle,
    setCompany,
    setJobDescription,
    refetchOriginalResumes
  };
};
